import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { ApiClientProvider } from '@platform/api-client/react';

import { AppErrorBoundary, RouteErrorFallback } from './components/error-boundary';
import { apiClient, queryClient } from './lib/api/client';
import { initSentry } from './lib/sentry';
import { routeTree } from './routeTree.gen';
import './styles.css';

/**
 * The client and the query cache are created together in `lib/api/client.ts`, because a
 * `401` from any endpoint has to write to the session cache — see the note there. No
 * persister is installed on that cache and none may be: anything written to localStorage
 * or IndexedDB would put PHI in the browser's storage (ADR-007).
 *
 * `ApiClientProvider` is the transport. The per-feature seams sit above it: integrations
 * talk to the real API through `lib/api/integrations.ts` (M3.9), while dashboard,
 * appointments, patients, forms and scheduling are still mock-backed until their
 * endpoints land. Each seam decides for itself in its own `*-client-context.tsx`.
 */

/* `defaultErrorComponent` is the route-level half of SR-54: a screen that throws
   loses itself and keeps the shell, instead of taking the whole app down with it.
   The fallback never renders `error.message` - see `components/error-boundary`. */
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultErrorComponent: RouteErrorFallback,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Before the first render, so an error thrown while mounting is still reported
// (M14.5). A no-op unless VITE_SENTRY_DSN is set; see lib/sentry.ts.
initSentry();

const container = document.getElementById('root');
if (container === null) {
  throw new Error('#root is missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ApiClientProvider client={apiClient}>
          <RouterProvider router={router} />
        </ApiClientProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>,
);
